import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import Logo from "../assets/Logo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { useGoogleLogin } from "@react-oauth/google";
import { createnewUser, loginUser } from "../services/userService";
import SocialIcon from "../assets/Social icon.svg";
import ForgetPasswordModal from "./forgetPasswordModal";
import { message, Input, Button } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import ReCAPTCHA from "react-google-recaptcha";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const { login } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);

  const handleCheckboxChange = () => {
    setChecked(!checked);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset();
      setCaptchaValue(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formRef.current.reportValidity()) {
      if (!captchaValue) {
        message.error("Please complete the captcha");
        return;
      }
      setIsLoading(true);
      const userData = {
        email,
        password,
        authProviderFlag: "local",
        captchaToken: captchaValue,
      };
      let loginSuccess = false;
      try {
        const response = await loginUser(userData);
        login(response);
        loginSuccess = true;
        message.success("Login Successful");
        if (response.onboarding) {
          navigate("/chat");
        } else {
          navigate("/onboarding");
        }
      } catch (error) {
        message.error(error.response?.data?.message || "Login failed");
        resetCaptcha();
      } finally {
        setIsLoading(false);
        if (loginSuccess) {
          setEmail("");
          setPassword("");
          setCaptchaValue(null);
        }
      }
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const { access_token } = tokenResponse;
      if (access_token) {
        let newUser;
        let userInfo;
        try {
          const response = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: {
                Authorization: `Bearer ${access_token}`,
              },
            }
          );
          userInfo = await response.json();
          newUser = {
            name: userInfo.name,
            email: userInfo.email,
            password: "",
            authProviderFlag: "google",
          };
          const userData = {
            email: userInfo.email,
            password: "",
            authProviderFlag: "google",
          };
          const loginResponse = await loginUser(userData);
          login(loginResponse);
          message.success("Login Successful");
          if (loginResponse.onboarding) {
            navigate("/chat");
          } else {
            navigate("/onboarding");
          }
        } catch (error) {
          if (error.response?.status === 404) {
            await createnewUser(newUser);
            const userData = {
              email: userInfo.email,
              password: "",
              authProviderFlag: "google",
            };
            const loginResponse = await loginUser(userData);
            login(loginResponse);
            message.success("Login Successful");
            if (loginResponse.onboarding) {
              navigate("/chat");
            } else {
              navigate("/onboarding");
            }
          } else {
            message.error(error.response?.data?.message || "Login failed");
          }
          setEmail("");
          setPassword("");
        }
      } else {
        console.log("Access token not found");
      }
    },
    onError: () => {
      console.log("Login Failed");
    },
  });

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");
    if (token) {
      axios
        .get(`https://api.protvguide.com/api/user/verify-email?token=${token}`)
        .then(() => {
          message.success("Email verified successfully! You can now log in.");
        })
        .catch(() => {
          message.error("Verification failed. The token may be invalid or expired.");
        });
    }
  }, [location]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  return (
    <div className="w-full min-h-full flex justify-center items-center py-[40px] ">
      {isOpen && (
        <ForgetPasswordModal isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
      <div className="flex flex-col w-[90%] xsm:w-[400px] md:w-[500px] gap-[20px] md:gap-[32px] py-[40px] md:py-[60px] px-[40px] md:px-[60px] shadow-2xl bg-white rounded-[32px]">
        <div className="flex justify-center items-center">
          <img
            src={Logo}
            alt="App Logo"
            className="w-[69.6px] h-[60px] md:w-[89.6px] md:h-[80px] cursor-pointer"
            onClick={() => window.location.href = "https://protvguide.com"}
          />
        </div>
        <div className="flex flex-col gap-[12px]">
          <h2 className="flex justify-center font-Poppins text-[24px] md:text-[30px] leading-[32px] md:leading-[38px] font-[700] text-center text-[#010101] ">
            Log into your account
          </h2>
          <p className="flex justify-center font-Poppins font-[400] text-[14px] md:text-[16px] leading-[22px] md:leading-[24px] text-center text-[#667085] ">
            Welcome back! Please enter your details.
          </p>
        </div>
        <div className="flex flex-col gap-[24px]">
          <div className="flex w-full">
            <form
              ref={formRef}
              onKeyDown={handleKeyPress}
              className="flex w-full flex-col gap-[20px]"
            >
              <div className="flex flex-col gap-[6px]">
                <label
                  className="flex text-[#344054] font-Poppins text-[14px] not-italic font-[500] leading-[20px]"
                  htmlFor="email"
                >
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  size="large"
                  style={{ padding: "10px" }}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="relative flex flex-col gap-[6px]">
                <label
                  className="flex text-[#344054] font-Poppins text-[14px] not-italic font-[500] leading-[20px]"
                  htmlFor="password"
                >
                  Password
                </label>
                <Input.Password
                  placeholder="Password"
                  value={password}
                  style={{ padding: "10px" }}
                  onChange={(e) => setPassword(e.target.value)}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeTwoTone twoToneColor="rgb(230, 31, 38)" />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: "rgb(230, 31, 38)" }} />
                    )
                  }
                  required
                />
              </div>
              {/* ✅ Captcha with ref for reset */}
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </form>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex gap-[8px]">
              <input
                type="checkbox"
                id="checkbox"
                checked={checked}
                onChange={handleCheckboxChange}
              />
              <span className="flex items-center text-[#344054] font-Poppins text-[12px] md:text-[14px] font-[500] leading-[18px] md:leading-[20px] ">
                Remember for 30 days
              </span>
            </div>
            <div className="flex">
              <button
                className="bg-transparent text-[#E61F26] font-Poppins text-[12px] md:text-[14px] font-[600] leading-[18px] md:leading-[20px]"
                onClick={() => setIsOpen(!isOpen)}
              >
                Forgot password
              </button>
            </div>
          </div>
          <div className="flex flex-col items-center w-full gap-[16px]">
            <div className="flex w-full">
              <Button
                type="primary"
                block
                onClick={handleSubmit}
                loading={isLoading}
                className="bg-[#E61F26] h-[45px]"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </div>
            <div className="flex w-full rounded-[8px] border-[1px] border-[#D0D5DD]">
              <button
                onClick={() => handleGoogleLogin()}
                className="w-full flex items-center justify-center gap-[12px] py-[10px] px-[16px] rounded-[8px] hover:bg-[#D0D5DD]"
              >
                <img src={SocialIcon} alt="Google Social Icon" />
                <span className="text-[#344054] font-Poppins text-[16px] font-[600] leading-[24px]">
                  Sign in with Google
                </span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full items-center gap-[12px]">
          <div className="flex w-full justify-center items-center gap-[4px]">
            <span className="text-[#667085] font-Poppins text-[14px] font-[400] leading-[20px] ">
              Don't have an account?
            </span>
            <button
              onClick={() => navigate("/signup")}
              className="flex text-[#E61F26] bg-transparent font-Poppins text-[14px] font-[600] leading-[20px]"
            >
              Sign up
            </button>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-[8px] text-center">
            <button
              onClick={() => navigate("/terms-of-use")}
              className="text-[#667085] hover:text-[#E61F26] font-Poppins text-[12px] font-[400] underline transition-colors"
            >
              Terms of Use
            </button>
            <span className="text-[#667085] font-Poppins text-[12px]">•</span>
            <button
              onClick={() => navigate("/privacy-policy")}
              className="text-[#667085] hover:text-[#E61F26] font-Poppins text-[12px] font-[400] underline transition-colors"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;